
/*
 * VNCtask : VNCtask – the easy to use Task Management & To-Do List application. Stay organized. Anytime! Anywhere!
 * Copyright (C) 2015-2020 VNC – Virtual Network Consult AG (info@vnc.biz)
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, version 3 of the License.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. Look for COPYING file in the top folder.
 * If not, see http://www.gnu.org/licenses/.
 */

import { Injectable, OnDestroy, inject } from "@angular/core";
import { TaskService } from "../task.service";
import { select, Store } from "@ngrx/store";
import { LoadTasks, LoadTasksSuccess, getIsTasksNextPageLoading, getIsTasksIsMoreTasks, getTasksCurrentPageOffset, NextLoadTasks, NextLoadTasksSuccess, getSelectedFilterOption, getIsSearchMode, getSortBy, LoadTasksFail, NextLoadTasksFail, StoreProjectList, StorePriorityList, StoreMemberList, CreateTask, CreateTaskSuccess, CreateTaskFail, RemoveTasksSuccess, RemoveTasksFail, UpdateTask, UpdateTasksSuccess, UpdateTaskFail, UpdateTaskSuccess, StoreAuthUser, getAuthUser, RemoveTasks, AllowLoadMore, CreateTasksSuccess, SetTaskStatisticsInfo, SetIsLoading, getSelectedFilterType, StoreTagList, getTagList, SetSearchStatisticsInfo, SetTagStatisticsInfo, UpdateReminderTasks, getReminderTasks, StoreFolderList, getFolderList, SetListStatisticsInfo, StoreMyTeamUsers, StoreSaveSearches, StoreLocationList, SetLocationStatisticsInfo, getLocationList, getAssignmentTasks, UpdateAssignmentTasks, getMemberList, SetTaskDetailHighlight, getTasks, SetTaskFilterType, SetTaskFilterOption, StoreSettings, getTasksById, getTaskStatisticsInfo, SetSelectAll, ResetSelectedTaskId, SetDetailView, SetEditTaskView, CompleteTask, AddNewTask as AddNewTaskAction, RemoveTasksRequest, DuplicateTasks as DuplicateTasksAction, UpdateBulkTasks as UpdateBulkTasksAction, UpdateBulkTasksRecurring as UpdateBulkTasksRecurringAction, UpdateTaskDetail } from "../store";
import { Task } from "../models/task";
import { TaskAttachment } from "../models/task-attachment";
import { TaskComment } from "../models/task-comment";
import { TasksRootState } from "../store/reducers/index";
import { TaskUtils } from "../shared/task-utils";
import { ErrorType, SuccessType, DateFilterType, BulkUpdateIssueType } from "../shared/task-enum";
import { TasksConstants } from "../shared/task-constacts";
import { Observable , of } from "rxjs";
import { SuccessService } from "../../common/providers/success-service";
import { ErrorService } from "../../common/providers/error-service";
import { TranslateService } from "@ngx-translate/core";
import { BulkUpdateArgs, AuthUser, User, FileUpload, TaskStatistics, Project, ICompact, MyUser, PendingOperation } from "../models/index";
import { ConfigService } from "../../common/providers/config.service";
import { Broadcaster } from "../../common/providers/broadcaster.service";
import { MessageTranslatorService } from "../services/message-translator-service";
import { LocalStorageService } from "../services/local-storage.service";
import { Subject, BehaviorSubject } from "rxjs";
import { DatabaseService } from "../services/database.service";
import { FilesStorageService } from "../services/files-storage.service";
import { CableService } from "../services/cable.service";
import { CommonUtil, MediaType, mobileonly } from "../../common/utils/common.utils";
import { AppConstants } from "../../common/utils/app-constants";
import { environment } from "../../../environments/environment";
import { IsDatabaseReady, getIsDeviceReady, getFederatedApps, getUserProfile, getAllContacts } from "../../reducers";
import { TaskNotificationType } from "../components/task-notifications";
import * as _ from "lodash";
import { getOnlineStatus, getLoginStatus } from "../../reducers";
import { Logout } from "../../actions/app";
import { List } from "../models/list";
import { VncLibraryService } from "@uxf/vnc-library";
import { ActivatedRoute, Router } from "@angular/router";
import { Location } from "../models/location";
import { taskAdapter } from "../store/reducers/tasks.reducers";
import { takeUntil, distinctUntilChanged, debounceTime, take, filter, tap, delay, catchError, switchMap, finalize, takeWhile, map } from "rxjs/operators";
import { TaskWatchersComponent } from "../components";
import { DomSanitizer } from "@angular/platform-browser";
import { ElectronService } from "../services/electron.service";
import { ContactInfo } from "src/app/shared/models/contact-info.model";
import { AddVNCContacts, BulkLoadVNCContacts } from "src/app/actions/contact.action";
import { StorageService } from "../../common/services/storage.service";

import { TaskRepositorySyncLayer } from "./task.repository-sync.layer";
import { TaskRepositoryStateBase } from "./task.repository-state";

// Mechanical decomposition layer of TaskRepository (Q-02). Behavior-preserving:
// fields/methods are unchanged, split across inheritance layers; the DI token
// remains the final TaskRepository class.
export abstract class TaskRepositoryQueryLayer extends TaskRepositorySyncLayer {


  protected getAllTags() {
    if (this.isOnline) {
      this.taskService.getAllTags().pipe(take(1)).subscribe(allTags => {
        this.setAllTagList(allTags);
      });
    } else if (CommonUtil.isSQLSupported()) {
      this.store.select(IsDatabaseReady).pipe(filter(ready => !!ready), take(1)).subscribe(ready => {
        this.databaseService.getAllTags().subscribe(allTags => {
          this.setAllTagList(allTags);
        });
      });
    }
  }





  public updateUserAvatar(img: string, userId: number, jid: string): void {
    this.taskService.updateUserAvatar(img).subscribe({
      next: res => {
        CommonUtil.getBase6ImageFromUrl(res).subscribe({
          next: (base64: string) => {
            const main = { id: userId, jid, data: base64 };
            this.databaseService.addAvatar(main).subscribe({
              next: () => {
                this.databaseService.getAllAvatar().subscribe({
                  next: (all: any[] = []) => {
                    const matches = all.filter(Update =>
                      Update?.jid && String(Update.jid).toLowerCase() === String(jid).toLowerCase()
                    );

                    let completed = 0;
                    matches.forEach(m => {
                      const updated = { ...m, data: base64 };
                      this.databaseService.addAvatar(updated).subscribe({
                        next: () => {
                          completed++;
                          if (completed === matches.length) {
                            let user = this.localStorageService.getUser();
                            if (user && user.id === userId) {
                              user.userAvatar = base64;
                              this.localStorageService.storeUser(user);
                              this.store.dispatch(new StoreAuthUser(user));
                            }
                            this.broadcaster.broadcast("profileAvatarUpdatedLocal", {
                              jid,
                              avatar: base64 + "?t=" + Date.now()
                            });
                            this.successService.emit({ id: SuccessType.UserAvatarUpdated, messages: this.msgs.PROFILE_UPDATED_MSG });
                          }
                        },
                        error: err => {
                          this.errorService.emit({ id: ErrorType.GenericMessage, messages: err });
                        }
                      });
                    });
                  },
                });
              },
            });
          },
          error: err => {
            this.errorService.emit({ id: ErrorType.GenericMessage, messages: err });
          },
        });
      },
    });
  }

  public removeUserAvatar() {
    this.taskService.removeUserAvatar().subscribe(res => {
      this.successService.emit({ id: SuccessType.UserAvatarUpdated, messages: this.msgs.PROFILE_REMOVED_MSG});
    }, err => {
      this.errorService.emit({ id: ErrorType.GenericMessage, messages: err });
    });
  }

  /** Entry point only (STR-07): TaskFlowEffects is the sole invoker of the flow body. */
  public addNewTask(payload: any) {
    this.store.dispatch(new AddNewTaskAction({ taskPayload: payload }));
  }







  removeTask(id: number) {
    this.store.dispatch(new RemoveTasksRequest({ ids: [id], successEvent: SuccessType.TaskRemoved }));
  }

  removeTasks(selectedIds: number[]): Observable<any> {
    this.store.dispatch(new RemoveTasksRequest({ ids: selectedIds, successEvent: SuccessType.TasksRemoved }));
    return;
  }


  /** Delegate: body moved to TaskFlowService (STR-07). */
  protected removeTasksSuccess(selectedIds: number[], successEvent: SuccessType){
    return this.flowService.removeTasksSuccess(selectedIds, successEvent);
  }

  /** Delegate: body moved to TaskFlowService (STR-07). */
  protected removeTasksError(err){
    return this.flowService.removeTasksError(err);
  }

  completeTask(task: Task, args: BulkUpdateArgs) {
    // Performed by TaskWriteEffects: the server call, the SQLite write, the pending-update
    // queue entry and the resulting success/fail action all live there now (STR-07).
    this.store.dispatch(new CompleteTask({ task, args }));
  }

  /** Entry point only (STR-07). The return value was never consumed by any caller. */
  duplicateTasks(tasks: Task[]): void {
    this.store.dispatch(new DuplicateTasksAction({ tasks }));
  }


  /** Delegate: body moved to TaskFlowService (STR-07). */
  duplicateTasksSuccess(createdTasks: Task[]){
    return this.flowService.duplicateTasksSuccess(createdTasks);
  }

  /** Delegate: body moved to TaskFlowService (STR-07). */
  protected duplicateTasksError(err){
    return this.flowService.duplicateTasksError(err);
  }

  /** Delegate: body moved to TaskFlowService (STR-07). */
  protected getTaskCopy(t: any): any {
    return this.flowService.getTaskCopy(t);
  }

  /** Entry point only (STR-07). The return value was never consumed by any caller. */
  updateBulkTasks(selectedTasks: Task[], fieldToUpdate: BulkUpdateIssueType, args: BulkUpdateArgs): Observable<any> {
    this.store.dispatch(new UpdateBulkTasksAction({ tasks: selectedTasks, fieldToUpdate, args }));
    return;
  }


  /** Delegate: body moved to TaskFlowService (STR-07). */
  protected updateBulkTasksOnDB(selectedTasks: Task[], fieldToUpdate: BulkUpdateIssueType, args: BulkUpdateArgs, valueToUpdate: any) {
    return this.flowService.updateBulkTasksOnDB(selectedTasks, fieldToUpdate, args, valueToUpdate);
  }

  /** Entry point only (STR-07). */
  updateBulkTasksRecurring(selectedTasks: Task[], type: BulkUpdateIssueType, item: BulkUpdateArgs) {
    this.store.dispatch(new UpdateBulkTasksRecurringAction({ tasks: selectedTasks, fieldToUpdate: type, args: item }));
  }


  /** Delegate: body moved to TaskFlowService (STR-07). */
  protected updateBulkTasksRecurringOnDB(selectedTasks: Task[], type: BulkUpdateIssueType, item: BulkUpdateArgs) {
    return this.flowService.updateBulkTasksRecurringOnDB(selectedTasks, type, item);
  }

  navigateToTaskFromNotification(notifiTaskId, notifiTaskUpdatedOn, notifiType, appInBackground) {
    console.log("[TaskRepository][navigateToTaskFromNotification]", notifiTaskId, notifiTaskUpdatedOn, notifiType, appInBackground);

    let qp: any = { openedFromNotification: 1 };

    if (notifiType === TaskNotificationType.TASK_UPDATE || notifiType === TaskNotificationType.TASK_WATCHER ) {
      qp.taskUpdatedOn = notifiTaskUpdatedOn;
      this.store.dispatch(new SetTaskDetailHighlight(true));
    } else {
      this.store.dispatch(new SetTaskDetailHighlight(false));
    }

    if (notifiType === TaskNotificationType.OVERDUE_TASKS) {
      this.router.navigate(["/task/overdue"], {});
    } else if (notifiType === TaskNotificationType.TODAY_DUE_TASKS) {
      this.router.navigate(["/task/today"], {});
    } else {
      this.router.navigate(["/task/all/detail/" + notifiTaskId], { queryParams: qp });
    }
  }

  public getTaskDetails(id: number, taskUpdatedOn: Date = null): Observable<Task> {
    console.log("TaskRepository getTaskDetails: ", id, taskUpdatedOn);

    const response = new Subject<Task>();

    // Mobile app
    if (CommonUtil.isSQLSupported()) {
      this.store.select(IsDatabaseReady).pipe(filter(ready => !!ready), take(1)).subscribe(ready => {
        this.databaseService.getTask(id).subscribe(task => {
          if (task) {
            if (!taskUpdatedOn || task.updated_on.getTime() === taskUpdatedOn.getTime()) {
              response.next(task);
            } else {
              // this could happen when we click on push notification about task updated, but have old task data in SQLite
              // so need to sync with backend first
              if (this.isOnline) {
                this.taskService.getTaskDetails(id).subscribe(task => {
                  // need to clone task object,
                  // because it's mutable object and it made some issues with start/due date's fields in task details view
                  const clonedTask = JSON.parse(JSON.stringify(task));
                  this.databaseService.saveOrUpdateTasks([clonedTask]).subscribe(dbRes => {
                    response.next(task);
                  });
                }, err => {
                  response.next(null);
                });
              } else {
                response.next(task);
              }
            }
          } else {
            // this could happen when we click on push notification about new task assignment
            // so need to sync with backend first
            this.taskService.getTaskDetails(id).subscribe(task => {
              // need to clone task object,
              // because it's mutable object and it made some issues with start/due date's fields in task details view
              const clonedTask = JSON.parse(JSON.stringify(task));
              this.databaseService.addNewTask(clonedTask).subscribe(dbRes => {
                response.next(task);
              });
            }, err => {
              response.next(null);
            });
          }
        });
      });
    // Web client
    } else {
      if (this.isOnline) {
        this.taskService.getTaskDetails(id).subscribe(res => {
          response.next(res);
        }, err => {
          response.next(null);
        });
      }
      else {
        this.databaseService.getTaskDetailsById(id).subscribe(res => {
          response.next(res);
        }, err => {
          response.next(null);
        });
      }
    }

    return response.asObservable().pipe(take(1));
  }

  /** Entry point only (STR-07). */
  updateTaskDetail(oldTask: Task, newTask: Task, fileUploads: FileUpload[], watcherMembers: User[]) {
    this.store.dispatch(new UpdateTaskDetail({ oldTask, newTask, fileUploads, watcherMembers }));
  }







  /** Delegate: body moved to TaskFlowService (STR-07). */
  protected updateTaskError(oldTask, err) {
    return this.flowService.updateTaskError(oldTask, err);
  }

  addComment(task: Task, notes: string): Observable<any> {
    console.log("[TaskRepository] addComment. task & notes: ", task, notes);

    const response = new Subject<any>();

    this.store.dispatch(new SetIsLoading(true));

    if (!this.isOnline && CommonUtil.isSQLSupported()) {
      let taskComment = TaskComment.commentCreateoffline(notes, { id: this.authUser.id, name: this.authUser.fullname });
      this.databaseService.addPendingTaskCommentCreate(task.id, taskComment).pipe(finalize(() =>
        this.store.dispatch(new SetIsLoading(false))
      )).subscribe( res => {
        taskComment.notes = taskComment.notes.replace(/<br>/g, "\n");
        taskComment.notes = taskComment.notes.replace(/(?:\r\n|\r|\n)/g, "<br>");
        taskComment.notes = this.renderMentionUsers(taskComment.notes, this.memberList);
        taskComment.notes = CommonUtil.linkify(taskComment.notes);
        task.comments.push(taskComment);
        // add on DB
        if (CommonUtil.isSQLSupported()) {
          this.databaseService.updateComments(task).subscribe(res => {
            response.next(res);
          });
        } else {
          response.next(res);
        }
      });
    } else {
      // add on server
      this.taskService.addCommentFor(task.id, notes).pipe(finalize(() =>
        this.store.dispatch(new SetIsLoading(false))
      )).subscribe(res => {
        console.log("[TaskRepository] addComment res success", res);

        let comment = new TaskComment({
          id: res.id,
          notes: res.notes,
          user: res.user,
          created_on: new Date(res.created_on)
        });
        comment.notes = comment.notes.replace(/<br>/g, "\n");
        comment.notes = comment.notes.replace(/(?:\r\n|\r|\n)/g, "<br>");
        comment.notes = this.renderMentionUsers(comment.notes, this.memberList);
        comment.notes = CommonUtil.linkify(comment.notes);
        task.comments.push(comment);
        task.lock_version = res.lock_version;
        // add on DB
        if (CommonUtil.isSQLSupported()) {
          this.databaseService.updateComments(task).subscribe(res => {
            response.next(res);
          });
        } else {
          response.next(res);
        }
      }, err => {
        console.log("[TaskRepository] addComment error", err);

        this.store.dispatch(new UpdateTaskFail());
        if ( err && err.includes(TasksConstants.MUST_BE_GREATER_THEN) ) {
          this.errorService.emit({ id: ErrorType.GenericMessage, messages: this.msgs.START_DATE_CHANGE_ERROR_MESSAGE });
        } else {
          this.errorService.emit({ id: ErrorType.GenericMessage, messages: err });
        }
        response.next(false);
      });
    }
    return response.asObservable().pipe(take(1));
  }

  editComment(task: Task, currentComment: TaskComment, notes: string): Observable<any> {
    console.log("[TaskRepository] editComment. task & currentComment & notes: ", task, currentComment, notes);

    const response = new Subject<any>();

    this.store.dispatch(new SetIsLoading(true));

    let newComment = new TaskComment({ id: currentComment.id, user: currentComment.user, notes: notes, created_on: currentComment.created_on });

    if (!this.isOnline && CommonUtil.isSQLSupported()) {
      this.databaseService.addPendingTaskCommentUpdate(task.id, newComment).pipe(finalize(() =>
        this.store.dispatch(new SetIsLoading(false))
      )).subscribe( res => {
        currentComment.notes = newComment.notes.replace(/<br>/g, "\n");
        currentComment.notes = currentComment.notes.replace(/(?:\r\n|\r|\n)/g, "<br>");
        currentComment.notes = this.renderMentionUsers(currentComment.notes, this.memberList);
        currentComment.notes = CommonUtil.linkify(currentComment.notes);
        currentComment.isEditMode = false;
        currentComment.isEdited = true;
        // add on DB
        if (CommonUtil.isSQLSupported()) {
          this.databaseService.updateComments(task).subscribe(res => {
            response.next(res);
          });
        } else {
          response.next(res);
        }
      });
    } else {
      this.taskService.editCommentFor(newComment).pipe(finalize(() =>
        this.store.dispatch(new SetIsLoading(false))
      )).subscribe(res => {
        console.log("[TaskRepository] editComment res success", res);
        currentComment.notes = newComment.notes.replace(/<br>/g, "\n");
        currentComment.notes = currentComment.notes.replace(/(?:\r\n|\r|\n)/g, "<br>");
        currentComment.notes = this.renderMentionUsers(currentComment.notes, this.memberList);
        currentComment.notes = CommonUtil.linkify(currentComment.notes);
        currentComment.isEditMode = false;
        currentComment.isEdited = true;

        // add on DB
        if (CommonUtil.isSQLSupported()) {
          this.databaseService.updateComments(task).subscribe(res => {
            response.next(res);
          });
        } else {
          response.next(res);
        }
      }, err => {
        console.log("[TaskRepository] editComment error", err);
        this.errorService.emit({ id: ErrorType.GenericMessage, messages: err });
        response.next(false);
      });
    }
    return response.asObservable().pipe(take(1));
  }

  downloadFile(attachment: TaskAttachment): void{
    console.log("[TaskRepository] downloadFile");

    if (environment.isCordova) {
      const message = this.messageTranslatorService.getMessage(TasksConstants.DOWNLOADING_STARTED);
      this.vncLibaryService.openSnackBar(message, "","", "", 3000, "bottom", "left").subscribe(res => {});
    }
    this.taskService.downloadFileAsBlob(attachment).subscribe(blob => {
      this.downloadFileFromBlob(blob, attachment.filename);
    }, err => {
      console.log("[TaskRepository] downloadFile err", err);
    });
  }

  downloadFileFromBlob(blob: Blob, filename: string) {
    if (CommonUtil.isOnNativeMobileDevice()) {
      this.downloadFileFromCordova(blob, filename)
        .subscribe(res => {
          /* cordova.plugins.notification.local.schedule({
            id: new Date().getTime(),
            title: filename,
            text: this.messageTranslatorService.getMessage(TasksConstants.DOWNLOAD_SUCCESS)
          }); */
        });
    } else {
      let url = window.URL.createObjectURL(blob);
      let a = document.createElement("a");
      document.body.appendChild(a);
      a.setAttribute("style", "display: none");
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    }
  }

  protected downloadFileFromCordova(blob: Blob, fileName: string): any {
    const response = new Subject<string>();

    if (CommonUtil.isOnIOS()) {
      // save in hidden cache
      this.filesStorageService.saveBlobToDisc(blob, fileName, false).subscribe((localFileUrl)  => {
        // for iOS we need to additionaly save to Camera Roll
        if (CommonUtil.getMediaType(fileName) === this.MediaType.IMAGE) {
          cordova.plugins.imagesaver.saveImageToGallery(localFileUrl, () => {
            response.next(localFileUrl);
          }, (err) => {
            console.log("[TaskRepository] downloadImageFileFromCordova err", err);
          });
        }
        if (CommonUtil.getMediaType(fileName) === this.MediaType.VIDEOS) {
          cordova.plugins.saveVideoToGallery(localFileUrl, () => {
            response.next(localFileUrl);
          }, (err) => {
            console.log("[TaskRepository] downloadVideoFileFromCordova err3", err);
          });
        }
      }, err => {
        response.error(err);
      });
    } else {
      // save in hidden cache
      this.filesStorageService.saveBlobToDisc(blob, fileName).subscribe((localFileUrl)  => {
        // now save into Download folder
        this.filesStorageService.saveBlobToAndroidDownloadFolder(blob, fileName).subscribe((localFileUrl)  => {
          response.next(localFileUrl);
        }, err => {
          response.error(err);
        });
      }, err => {
        response.error(err);
      });
    }

    return response.asObservable().pipe(take(1));
  }

  getAttachmentContent(attachment): Observable<any> {
    return this.taskService.getAttachmentContent(attachment);
  }

  public reloadApp() {
    if (this.isOnline && CommonUtil.isSQLSupported()) {
      this.syncTasksWithBackend();
    } else {
      this.getTasks();
    }
    this.getTasksStats();
    this.getTagsListWithCounters();
    this.getFolderListWithCounters();
    this.getLocationsWithCounters();
  }



  public userRegistration(body: any) {
    this.taskService.userRegisration(body).subscribe( res => {
      this.successService.emit({ id: SuccessType.UserRegistered, messages: "" });
    }, err => {
      if ( err && (err === "Erlaubnis abgelehnt" || err === "Permission declined")) {
        this.errorService.emit({ id: ErrorType.UserRegisteredError, messages: this.msgs.NOT_ALLOWED_REGISTER });
      } else {
        this.errorService.emit({ id: ErrorType.UserRegisteredError, messages: this.msgs.NEW_REGISTER_ERROR_MESSAGE });
      }
    });
  }

  public resendActivationLink(mail: string) {
    this.taskService.resendActivationLink(mail).subscribe( res => {
      this.successService.emit({ id: SuccessType.ResendActivationLinkSuccess, messages: this.msgs.ACTIVATION_LINK_SEND_SUCCESS });
    }, err => {
      this.errorService.emit({ id: ErrorType.ResendActivationLinkError, messages: err });
    });
  }

  public resetPassword(token: string, password: string, confirmPassword: string) {
    this.taskService.resetPassword(token, password, confirmPassword).subscribe( res => {
      this.successService.emit({ id: SuccessType.ResetPasswordSuccess, messages: this.msgs.RESET_PASSWORD_SUCCESS });
    }, err => {
      this.errorService.emit({ id: ErrorType.ResetPasswordError, messages: this.msgs.RESET_PASSWORD_ERROR });
    });
  }

  public forgotPassword(mail: string) {
    this.taskService.forgotPassword(mail).subscribe( res => {
      if ( res && res.includes(TasksConstants.UNKNOWN)) {
        this.errorService.emit({ id: ErrorType.ForgotPasswordError, messages: this.msgs.ACCOUNT_NOT_FOUND_WITH_EMAIL });
      } else {
        this.successService.emit({ id: SuccessType.ForgotPasswordSuccess, messages: this.msgs.FORGOT_PASSWORD_SEND_SUCCESS });
      }
    }, err => {
      if ( err && err.includes(TasksConstants.HAVE_NOT_ACTIVATED) ) {
        this.errorService.emit({ id: ErrorType.ForgotPasswordError, messages: this.msgs.HAVE_NOT_ACTIVATED_MSG });
      } else if ( err && err.includes(TasksConstants.LOCKED) ) {
        this.errorService.emit({ id: ErrorType.ForgotPasswordError, messages: this.msgs.ACCOUNT_LOCKED_MSG });
      } else {
        this.errorService.emit({ id: ErrorType.ForgotPasswordError, messages: this.msgs.ACCOUNT_NOT_FOUND_WITH_EMAIL });
      }
    });
  }

  public changePassword(password: string, new_password: string, new_password_confirmation: string) {
    this.taskService.changePassword(password, new_password, new_password_confirmation).subscribe( res => {
      this.successService.emit({ id: SuccessType.ChangePasswordSuccess, messages: this.msgs.CHANGE_PASSWORD_SUCCESS });
    }, err => {
      if (err && err.includes("Wrong current password")) {
        this.errorService.emit({ id: ErrorType.ChangePasswordError, messages: this.msgs.WRONG_CURRENT_PWD_ERROR });
      } else {
        this.errorService.emit({ id: ErrorType.ChangePasswordError, messages: this.msgs.CHANGE_PASSWORD_ERROR });
      }
    });
  }

  public deleteAccount(feedback: string, password: string) {
    this.taskService.deleteAccount(feedback, password).subscribe( res => {
      this.successService.emit({ id: SuccessType.DeleteAccountSuccess, messages: this.msgs.DEACTIVATE_ACCOUNT_SUCCESS });
    }, err => {
      if (err && err.includes("Wrong password")) {
        this.errorService.emit({ id: ErrorType.DeleteAccountError, messages: this.msgs.WRONG_CURRENT_PWD_ERROR });
      } else {
        this.errorService.emit({ id: ErrorType.DeleteAccountError, messages: err });
      }
    });
  }

  public removeLoginIframe() {
    if (document.querySelector("#loginIframe") !== null) {
      document.querySelector("#loginIframe").remove();
    }
    if (document.querySelector("#logoutIframe") !== null) {
      document.querySelector("#logoutIframe").remove();
    }
  }

  public errorSubjectRequired() {
    this.errorService.emit({ id: ErrorType.GenericMessage, messages: this.msgs.TASK_SUBJECT_REQUIRED });
  }

  public subjectNotAllowed() {
    this.errorService.emit({ id: ErrorType.GenericMessage, messages: this.msgs.TASK_SUBJECT_NOT_ALLOWED });
  }

  public subjectTooLong() {
    this.errorService.emit({ id: ErrorType.GenericMessage, messages: this.msgs.SUBJECT_IS_TOO_LONG });
  }

  getDataFromDatabase(taskFilter) {
    const offset = 0;
    const isMoreTasks = false;
    let routingType = "";
    this.store.select(getSelectedFilterType).pipe(take(1)).subscribe(o => routingType = o);
    if (routingType === "status") {
      switch (taskFilter) {
        case "all":
          this.databaseService.getAllTasks().subscribe(taskFromDB => {
            this.sortTaskFromDB(taskFromDB, offset, isMoreTasks);
          });
           break;
        case "open":
          this.databaseService.getOpenTasks().subscribe(taskFromDB => {
            this.sortTaskFromDB(taskFromDB, offset, isMoreTasks);
          });
          break;
        case "new":
          this.databaseService.getNewTasks().subscribe(taskFromDB => {
            this.sortTaskFromDB(taskFromDB, offset, isMoreTasks);
          });
          break;
        case "completed":
          this.databaseService.getCompletedTasks().subscribe(taskFromDB => {
            this.sortTaskFromDB(taskFromDB, offset, isMoreTasks);
          });
          break;
        case "watch":
          this.databaseService.getWatchTasks().subscribe(taskFromDB => {
            this.sortTaskFromDB(taskFromDB, offset, isMoreTasks);
          });
          break;
        case "overdue":
          this.databaseService.getOverdueTasks().subscribe(taskFromDB => {
            this.sortTaskFromDB(taskFromDB, offset, isMoreTasks);
          });
          break;
        case "week":
          this.databaseService.getWeekTasks().subscribe(taskFromDB => {
            this.sortTaskFromDB(taskFromDB, offset, isMoreTasks);
          });
          break;
        case "tomorrow":
          this.databaseService.getTomorrowTasks().subscribe(taskFromDB => {
            this.sortTaskFromDB(taskFromDB, offset, isMoreTasks);
          });
          break;
        case "assigned":
          this.databaseService.getAssignedTasks().subscribe(taskFromDB => {
            this.sortTaskFromDB(taskFromDB, offset, isMoreTasks);
          });
          break;
        case "today":
          this.databaseService.getTodayTasks().subscribe(taskFromDB => {
            this.sortTaskFromDB(taskFromDB, offset, isMoreTasks);
          });
          break;
        case "created":
          this.databaseService.getCreatedTasks().subscribe(taskFromDB => {
            this.sortTaskFromDB(taskFromDB, offset, isMoreTasks);
          });
          break;
      }
    }
    if (routingType === "tags") {
      this.databaseService.getTagsTasks(taskFilter).subscribe(taskFromDB => {
        this.sortTaskFromDB(taskFromDB, offset, isMoreTasks);
      });
    }
    if (routingType === "list") {
      this.databaseService.getListTasks(taskFilter).subscribe(taskFromDB => {
        this.sortTaskFromDB(taskFromDB, offset, isMoreTasks);
      });
    }
    if (routingType === "location") {
      this.databaseService.getLocationTasks(taskFilter).subscribe(taskFromDB => {
        this.sortTaskFromDB(taskFromDB, offset, isMoreTasks);
      });
    }
  }


  /** Delegate: body moved to TaskFlowService (STR-07). */
  public getStatusItems(): any {
    return this.flowService.getStatusItems();
  }


  public renderMentionUsers(content: string, members: User[]) {
    let mentions = CommonUtil.parseMentions(content);
    if (mentions.length > 0) {
      mentions.forEach(mention => {
        if (members && members.length > 0) {
          members.forEach( member => {
            if (member.username === mention) {
              content = content.replace(new RegExp("@" + mention, "gim"), `<span class="mentioned-user">@${mention}</span>`);
            }
          });
        }
      });
    }
    return content;
  }

  /** Delegate: body moved to TaskFlowService (STR-07). */
  syncBulkUpdateToDB(selectedTasks, fieldToUpdate, args, valueToUpdate) {
    return this.flowService.syncBulkUpdateToDB(selectedTasks, fieldToUpdate, args, valueToUpdate);
  }

  sortTaskFromDB(tasks, offset, isMoreTasks) {
    let sortBy;
    this.store.select(getSortBy).pipe(take(1)).subscribe(o => sortBy = o);
    switch (sortBy) {
      case "due_date":
        this.sortDataByDueDate(tasks);
        this.store.dispatch(new LoadTasksSuccess({ currOffset: offset, isMoreTasks: isMoreTasks, task: tasks }));
         break;
      case "priority":
        this.sortDataByPriority(tasks);
        this.store.dispatch(new LoadTasksSuccess({ currOffset: offset, isMoreTasks: isMoreTasks, task: tasks }));
        break;
      case "subject":
        this.sortDataByTaskName(tasks);
        this.store.dispatch(new LoadTasksSuccess({ currOffset: offset, isMoreTasks: isMoreTasks, task: tasks }));
        break;
      case "start_date":
        this.sortDataByStartDate(tasks);
        this.store.dispatch(new LoadTasksSuccess({ currOffset: offset, isMoreTasks: isMoreTasks, task: tasks }));
        break;
      case "status":
        this.sortDataByStatus(tasks);
        console.log(" => info:tasks ", tasks);
        this.store.dispatch(new LoadTasksSuccess({ currOffset: offset, isMoreTasks: isMoreTasks, task: tasks }));
        break;
      case "created_on":
        this.sortDataByCreatedOn(tasks);
        this.store.dispatch(new LoadTasksSuccess({ currOffset: offset, isMoreTasks: isMoreTasks, task: tasks }));
        break;
    }
  }

  sortDataByCreatedOn(tasks) {
    tasks.sort((a, b) => {
      const dateA = new Date(a.created_on);
      const dateB = new Date(b.created_on);
      const dateComparison = dateB.getTime() - dateA.getTime();
      if (dateComparison !== 0) {
        return dateComparison;
      }
      const timeA = dateA.getHours() * 60 + dateA.getMinutes();
      const timeB = dateB.getHours() * 60 + dateB.getMinutes();
      const minuteComparison = timeB - timeA;
      if (minuteComparison !== 0) {
        return minuteComparison;
      }
      return 0;
    });
  }

  sortDataByDueDate(tasks) {
    tasks.sort((a, b) => {
      if (!a.due_date) {
        return 1;
      }
      if (!b.due_date) {
        return -1;
      }
      const dateA = new Date(a.due_date);
      const dateB = new Date(b.due_date);
      const dateComparison = dateA.getTime() - dateB.getTime();
      if (dateComparison !== 0) {
        return dateComparison;
      }
      const timeA = dateA.getHours() * 60 + dateA.getMinutes();
      const timeB = dateB.getHours() * 60 + dateB.getMinutes();
      const minuteComparison = timeA - timeB;
      if (minuteComparison !== 0) {
        return minuteComparison;
      }
      return 0;
    });
  }

  sortDataByPriority(tasks) {
    return tasks.sort((a, b) => b.priority.id - a.priority.id);
  }

  sortDataByStartDate(tasks) {
    tasks.sort((a, b) => {
      if (!a.start_date) {
        return 1;
      }
      if (!b.start_date) {
        return -1;
      }
      const dateA = new Date(a.start_date);
      const dateB = new Date(b.start_date);
      const dateComparison = dateA.getTime() - dateB.getTime();
      if (dateComparison !== 0) {
        return dateComparison;
      }
      const timeA = dateA.getHours() * 60 + dateA.getMinutes();
      const timeB = dateB.getHours() * 60 + dateB.getMinutes();
      const minuteComparison = timeA - timeB;
      if (minuteComparison !== 0) {
        return minuteComparison;
      }
      return 0;
    });
  }

  sortDataByStatus(tasks) {
    return tasks.sort((a, b) => a.status.id - b.status.id);
  }

  sortDataByTaskName(tasks) {
    return tasks.sort((a, b) => a.subject.localeCompare(b.subject));
  }

}
